import React, { createContext, useContext, useRef, useState, useMemo, useCallback } from 'react';
import { useNavigationGuard } from '../../../common/hooks/useNavigationGuard';
import type {
  DiagramBpmnProps,
  DiagramContextValue,
  ElementModal,
  ModalType,
  // AiSolutionData,
} from '../Types/diagram-type';
import { AiSolutionData } from '../../TaxonomyXPLR/modules/ProcessXPLR';
const DiagramContext = createContext<DiagramContextValue | undefined>(undefined);

export const useDiagramContext = () => {
  const context = useContext(DiagramContext);
  if (!context) {
    throw new Error('useDiagramContext must be used within a DiagramProvider');
  }
  return context;
};

export const DiagramProvider: React.FC<DiagramBpmnProps & { children: React.ReactNode }> = ({
  children,
  diagramData,
  aiOpportunityData = [],
  isEditPermission = true,
  selectedCard,
  selectedSubprocess,
  selectedTaxonomy,
  selectedE2E,
  selectedSolution,
  selectedStage,
  selectedSolutionDescription,
  taxonomySubprocessName,
  taxonomySubprocessDescription,
  isProcessDiagram = false,
  opportunityName,
  kpis = {},
  useCaseData,
  lastSavedXmlRef = {},
  prevDiagramData = {},
  setPrevDiagramData = () => {},
  onResetAll,
  onResetTaxonomy,
  onResetE2E,
  onResetSolution,
  onDiagramSaved,
  onClose,
  onDiagramChange,
  isVersionViewMode = false,
  onRestoreVersion,
  onVersionHistoryClick,
}) => {
  // Refs
  const bpmnModelerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // UI State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentScale, setCurrentScale] = useState(1);
  const [showAllAiComponents, setShowAllAiComponents] = useState(false);
  const [hoveredElements, setHoveredElements] = useState<Set<string>>(new Set());
  const [isDiagramChanged, setIsDiagramChanged] = useState(false);
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [modal, setModal] = useState<ElementModal>({
    isOpen: false,
    elementData: null,
    activeTab: 'aiHubble',
    opportunities: [],
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFitViewToggled, setIsFitViewToggled] = useState(false);

  // Modal State
  const [isEnterpriseModalOpen, setIsEnterpriseModalOpen] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteIdeationModal, setShowDeleteIdeationModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showVaultModal, setShowVaultModal] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [favoriteModalOpen, setFavoriteModalOpen] = useState(false);

  // Data State
  const [solutionData, setSolutionData] = useState<unknown[]>([]);
  const [selectedSolutionFromLibrary, setSelectedSolutionFromLibrary] = useState({
    id: '',
    name: '',
    vault: false,
  });
  const [aiSolutionData, setAiSolutionData] = useState<AiSolutionData | null>(null);
  const [renameProcessName, setRenameProcessName] = useState('');
  const [opportunityUpdated, setOpportunityUpdated] = useState(false);
  const [enterpriseContextApplied, setEnterpriseContextApplied] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [aiEnablerData, setAiEnablerData] = useState<any>(null);
  const [dataSourceType, setDataSourceType] = React.useState<string>('internal');

  // Download state
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [downloadProgressOpen, setDownloadProgressOpen] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  // Handlers Registry
  const [handlers, setHandlers] = useState<Partial<DiagramContextValue>>({});

  const registerHandlers = useCallback((newHandlers: Partial<DiagramContextValue>) => {
    setHandlers((prev) => ({ ...prev, ...newHandlers }));
  }, []);

  const {
    isModalOpen: isNavigationGuardModalOpen,
    confirmNavigation,
    cancelNavigation,
  } = useNavigationGuard({
    shouldBlock: isDiagramChanged,
  });

  const contextValue = useMemo<DiagramContextValue>(
    () => ({
      // Props
      diagramData,
      aiOpportunityData,
      isEditPermission,
      selectedCard: selectedCard,
      selectedSubprocess: selectedSubprocess ?? null,
      selectedTaxonomy: selectedTaxonomy ?? null,
      selectedE2E: selectedE2E ?? null,
      selectedSolution: selectedSolution ?? null,
      selectedStage,
      selectedSolutionDescription,
      taxonomySubprocessName,
      taxonomySubprocessDescription,
      isProcessDiagram,
      opportunityName,
      kpis,
      useCaseData,
      lastSavedXmlRef,
      prevDiagramData,
      setPrevDiagramData,
      onResetAll,
      onResetTaxonomy,
      onResetE2E,
      onResetSolution,
      onDiagramSaved,
      onClose,
      onDiagramChange,
      isVersionViewMode,
      onRestoreVersion,
      onVersionHistoryClick,
      dataSourceType,
      // Refs
      bpmnModelerRef,
      containerRef,

      // UI State
      loading,
      setLoading,
      error,
      setError,
      currentScale,
      setCurrentScale,
      showAllAiComponents,
      setShowAllAiComponents,
      hoveredElements,
      setHoveredElements,
      isDiagramChanged,
      setIsDiagramChanged,
      activeModal,
      setActiveModal,
      modal,
      setModal,
      isExpanded,
      setIsExpanded,
      isFitViewToggled,
      setIsFitViewToggled,
      setDataSourceType,

      // Modal State
      isEnterpriseModalOpen,
      setIsEnterpriseModalOpen,
      showUnsavedChangesModal,
      setShowUnsavedChangesModal,
      showDeleteModal,
      setShowDeleteModal,
      showDeleteIdeationModal,
      setShowDeleteIdeationModal,
      showRenameModal,
      setShowRenameModal,
      showVaultModal,
      setShowVaultModal,
      isEditModalOpen,
      setIsEditModalOpen,
      favoriteModalOpen,
      setFavoriteModalOpen,

      // Data State
      solutionData,
      setSolutionData,
      selectedSolutionFromLibrary,
      setSelectedSolutionFromLibrary,
      aiSolutionData,
      setAiSolutionData,
      renameProcessName,
      setRenameProcessName,
      opportunityUpdated,
      setOpportunityUpdated,
      enterpriseContextApplied,
      setEnterpriseContextApplied,
      saveError,
      setSaveError,
      loadingDocument,
      setLoadingDocument,
      downloadProgressOpen,
      setDownloadProgressOpen,
      downloadProgress,
      setDownloadProgress,
      aiEnablerData,
      setAiEnablerData,

      // Handlers (delegated to registered implementations)
      handleSaveDiagram: handlers.handleSaveDiagram || (async () => {}),
      handleDiscardChanges: handlers.handleDiscardChanges || (async () => {}),
      handleClose: handlers.handleClose || (() => {}),
      handleZoomIn: handlers.handleZoomIn || (() => {}),
      handleZoomOut: handlers.handleZoomOut || (() => {}),
      handleFitViewToggle: handlers.handleFitViewToggle || (() => {}),
      handleStepDescriptionUpdate: handlers.handleStepDescriptionUpdate || (() => {}),
      registerHandlers,
      isNavigationGuardModalOpen,
      confirmNavigation,
      cancelNavigation,
    }),
    [
      diagramData,
      aiOpportunityData,
      isEditPermission,
      selectedCard,
      selectedSubprocess,
      selectedTaxonomy,
      selectedE2E,
      selectedSolution,
      selectedStage,
      selectedSolutionDescription,
      taxonomySubprocessName,
      taxonomySubprocessDescription,
      isProcessDiagram,
      opportunityName,
      kpis,
      useCaseData,
      lastSavedXmlRef,
      prevDiagramData,
      setPrevDiagramData,
      onResetAll,
      onResetTaxonomy,
      onResetE2E,
      onResetSolution,
      onDiagramSaved,
      onClose,
      onDiagramChange,
      isVersionViewMode,
      onRestoreVersion,
      onVersionHistoryClick,
      bpmnModelerRef,
      containerRef,
      loading,
      error,
      currentScale,
      showAllAiComponents,
      hoveredElements,
      isDiagramChanged,
      activeModal,
      isExpanded,
      isFitViewToggled,
      isEnterpriseModalOpen,
      showUnsavedChangesModal,
      showDeleteModal,
      showDeleteIdeationModal,
      showRenameModal,
      showVaultModal,
      isEditModalOpen,
      favoriteModalOpen,
      solutionData,
      selectedSolutionFromLibrary,
      aiSolutionData,
      renameProcessName,
      opportunityUpdated,
      enterpriseContextApplied,
      saveError,
      loadingDocument,
      downloadProgressOpen,
      downloadProgress,
      aiEnablerData,
      handlers, // Include handlers in dependency array
      isNavigationGuardModalOpen,
      confirmNavigation,
      cancelNavigation,
    ]
  );

  return <DiagramContext.Provider value={contextValue}>{children}</DiagramContext.Provider>;
};
